/**
 * The one shelf-life instruction, shared by every prompt that names food.
 *
 * It started as a block inside the fridge-photo prompt, which was the only
 * estimator in the app that looked at what the food actually *was*. The receipt
 * prompt had its own near-copy with different examples, and the delivery-email
 * prompt had none at all — which is how a whole chicken came to be dated two
 * weeks out. One block, four callers, one vocabulary.
 *
 * The buckets are deliberately coarse. A model can reliably tell "raw chicken
 * spoils in days" from "an onion keeps for weeks"; asking it for a day count
 * invites confident nonsense. `shelfLifeService` turns the bucket into days,
 * and the checked-in table overrules it whenever it recognises the food.
 */

export const SHELF_LIFE_PROMPT_BLOCK = `Shelf Life Estimation:
   • REQUIRED for every food item — assign exactly one bucket
   • Buckets:
     - "highly-perishable" (1-3 days): raw poultry, ground meat, fresh fish and seafood, all leftovers and cooked dishes, cut leafy greens, raspberries and other soft berries, anything visibly wilting or browning
     - "perishable" (4-7 days): whole cuts of raw meat, soft and fresh cheeses, opened tofu or hummus, delicate produce (asparagus, mushrooms, spinach, fresh herbs), opened yogurt or milk
     - "week-plus" (8-14 days): sealed dairy (milk, yogurt, cream), eggs, bread, sealed deli meat and bacon, firm produce (peppers, cucumber, zucchini, broccoli)
     - "keeps-weeks" (21-60 days): onions, garlic, potatoes, winter squash, carrots and root vegetables, apples and citrus, hard cheese, SEALED tofu, kombucha, cured sausage
     - "shelf-stable": sugar, maple syrup, honey, oil, vinegar, spices, dry pasta and rice, flour, canned and jarred goods, unopened condiments, nut butter, coffee, tea
   • Judge the FOOD, not the aisle it came from. A whole chicken is "highly-perishable"; a red onion from the same order is "keeps-weeks".
   • Sealed and opened are different foods. Report "opened": true when the package is open, the item is cut, or it is obviously a partly-used container; report false when it is intact or you cannot tell. Sealed tofu is "keeps-weeks", opened tofu is "perishable".
   • A flavour name in front of a product does not change the product: "raspberry kombucha" is kombucha, "maple syrup yogurt" is yogurt.
   • Frozen items: report the bucket the food would have fresh. Freezing is handled from the storage location, not from this field.`;

/** Worked examples, for prompts that read a product list rather than an image. */
export const SHELF_LIFE_PROMPT_EXAMPLES = `Shelf life examples:
   - "Whole chicken" / "Poulet entier" -> "highly-perishable"
   - "Ground walleye" / "Doré haché" -> "highly-perishable"
   - "Red onions" / "Oignons rouges" -> "keeps-weeks"
   - "Extra firm tofu" (sealed) -> "keeps-weeks"
   - "Maple syrup yogurt" -> "week-plus"
   - "Passion fruit kombucha" -> "keeps-weeks"
   - "Organic cane sugar" -> "shelf-stable"
   - "Black Forest ham" (sealed) -> "week-plus"
   - "Beefsteak tomatoes" -> "perishable"`;
